import React, { useEffect, useMemo, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import DateInput from "../../components/DateInput";
import {
  getRevenueSummary,
  getRevenueTransactions,
  type RevenueSummary,
  type RevenueTx,
  type RevenueTxType,
} from "../../services/revenue";

// All figures from the API are already RON cents.
const ron = (cents: number) =>
  `${(cents / 100).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} RON`;

const monthLabel = (ym: string) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("ro-RO", { month: "short", year: "numeric" });
};

const iso = (d: Date) => d.toISOString().split("T")[0];

type Preset = "month" | "30d" | "3m" | "ytd" | "12m" | "custom";

const STREAMS: { key: "athlete_plans" | "lives" | "club_invoices"; label: string; color: string; bg: string }[] = [
  { key: "athlete_plans", label: "Abonamente sportivi", color: "#4f46e5", bg: "bg-indigo-500" },
  { key: "lives", label: "Vieți", color: "#0d9488", bg: "bg-teal-500" },
  { key: "club_invoices", label: "Facturi club", color: "#d97706", bg: "bg-amber-500" },
];

const TYPE_FILTERS: { key: RevenueTxType; label: string }[] = [
  { key: "all", label: "Toate" },
  { key: "athlete_plans", label: "Abonamente sportivi" },
  { key: "lives", label: "Vieți" },
  { key: "club_invoices", label: "Facturi club" },
  { key: "affiliate", label: "Afiliați (cost)" },
];

export default function Revenue() {
  const now = new Date();
  const [preset, setPreset] = useState<Preset>("12m");
  const [from, setFrom] = useState(iso(new Date(now.getFullYear(), now.getMonth() - 11, 1)));
  const [to, setTo] = useState(iso(now));
  const [summary, setSummary] = useState<RevenueSummary | null>(null);
  const [txs, setTxs] = useState<RevenueTx[]>([]);
  const [txType, setTxType] = useState<RevenueTxType>("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const applyPreset = (p: Preset) => {
    setPreset(p);
    const d = new Date();
    if (p === "month") { setFrom(iso(new Date(d.getFullYear(), d.getMonth(), 1))); setTo(iso(d)); }
    else if (p === "30d") { const s = new Date(d); s.setDate(s.getDate() - 29); setFrom(iso(s)); setTo(iso(d)); }
    else if (p === "3m") { setFrom(iso(new Date(d.getFullYear(), d.getMonth() - 2, 1))); setTo(iso(d)); }
    else if (p === "ytd") { setFrom(iso(new Date(d.getFullYear(), 0, 1))); setTo(iso(d)); }
    else if (p === "12m") { setFrom(iso(new Date(d.getFullYear(), d.getMonth() - 11, 1))); setTo(iso(d)); }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [s, t] = await Promise.all([
          getRevenueSummary(from, to),
          getRevenueTransactions(from, to, txType),
        ]);
        if (!cancelled) { setSummary(s); setTxs(t); }
      } catch (e: any) {
        if (!cancelled) setError(e?.response?.data?.error || "Nu am putut încărca veniturile.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [from, to, txType]);

  const maxMonth = useMemo(() => {
    if (!summary) return 1;
    return Math.max(1, ...summary.months.map((m) => m.athlete_plans + m.lives + m.club_invoices));
  }, [summary]);

  const t = summary?.totals;
  const c = summary?.counts;

  const presetBtns: [Preset, string][] = [
    ["month", "Luna curentă"], ["30d", "Ultimele 30 zile"], ["3m", "Ultimele 3 luni"],
    ["ytd", "Anul curent"], ["12m", "Ultimele 12 luni"],
  ];

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="mx-auto max-w-[1500px]">
        <AdminPageHeader
          eyebrow="Platformă"
          title="Venituri"
          subtitle="Toate veniturile platformei — abonamente sportivi, vieți, facturi club și afiliați — normalizate în RON, filtrabile pe perioadă și tip"
          icon={
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />

        {/* Period filters */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-4 flex flex-wrap items-end gap-4">
          <div className="flex flex-wrap gap-2">
            {presetBtns.map(([p, label]) => (
              <button
                key={p}
                onClick={() => applyPreset(p)}
                className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  preset === p ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex items-end gap-3 ml-auto">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">De la</label>
              <DateInput mode="date" value={from} onChange={(v) => { setFrom(v); setPreset("custom"); }}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-800" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Până la</label>
              <DateInput mode="date" value={to} onChange={(v) => { setTo(v); setPreset("custom"); }}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-800" />
            </div>
          </div>
        </div>

        {error && <div className="rounded-xl bg-rose-50 border border-rose-200 p-3 text-sm text-rose-700 mb-6">{error}</div>}

        {/* Headline: gross + net */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
          <div className="bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-2xl p-5 text-white shadow-sm">
            <p className="text-xs font-medium text-indigo-100">Venit brut (RON)</p>
            <p className="mt-2 text-3xl font-extrabold tabular-nums">{loading || !t ? "—" : ron(t.gross_ron)}</p>
            <p className="mt-1 text-[11px] text-indigo-100/80">Abonamente + vieți + facturi club, încasate în perioadă</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
            <p className="text-xs font-medium text-slate-500">Venit net (după costuri afiliați)</p>
            <p className="mt-2 text-3xl font-extrabold text-slate-900 tabular-nums">{loading || !t ? "—" : ron(t.net_ron)}</p>
            <p className="mt-1 text-[11px] text-slate-400">
              Brut − {t ? ron(t.affiliate_cost_ron) : "—"} costuri afiliați
            </p>
          </div>
        </div>

        {/* Per-stream cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {STREAMS.map((s) => {
            const val = t ? (s.key === "athlete_plans" ? t.athlete_plans_ron : s.key === "lives" ? t.lives_ron : t.club_invoices_ron) : 0;
            const cnt = c ? c[s.key] : 0;
            const share = t && t.gross_ron > 0 ? Math.round((val / t.gross_ron) * 100) : 0;
            return (
              <div key={s.key} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
                  <p className="text-xs font-medium text-slate-500">{s.label}</p>
                </div>
                <p className="mt-2 text-2xl font-extrabold text-slate-900 tabular-nums">{loading || !t ? "—" : ron(val)}</p>
                <p className="mt-1 text-[11px] text-slate-400">{cnt} {cnt === 1 ? "tranzacție" : "tranzacții"} · {share}% din brut</p>
              </div>
            );
          })}
          {/* Affiliates: revenue generated + cost */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-fuchsia-500" />
              <p className="text-xs font-medium text-slate-500">Afiliați</p>
            </div>
            <p className="mt-2 text-lg font-extrabold text-slate-900 tabular-nums">
              {loading || !t ? "—" : ron(t.affiliate_revenue_ron)} <span className="text-[11px] font-medium text-slate-400">venit generat</span>
            </p>
            <p className="mt-1 text-sm font-bold text-rose-600 tabular-nums">
              −{loading || !t ? "—" : ron(t.affiliate_cost_ron)} <span className="text-[11px] font-medium text-slate-400">cost comisioane</span>
            </p>
          </div>
        </div>

        {/* Monthly breakdown */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-slate-700">Defalcare lunară (RON)</h3>
            <div className="flex items-center gap-3 text-[11px] text-slate-500">
              {STREAMS.map((s) => (
                <span key={s.key} className="inline-flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ background: s.color }} />{s.label}
                </span>
              ))}
            </div>
          </div>
          {loading ? (
            <p className="text-sm text-slate-500">Se încarcă...</p>
          ) : !summary || summary.months.length === 0 ? (
            <p className="text-sm text-slate-500">Nu există venituri în perioada selectată.</p>
          ) : (
            <div className="space-y-2.5">
              {summary.months.map((m) => {
                const total = m.athlete_plans + m.lives + m.club_invoices;
                const pct = (v: number) => (maxMonth > 0 ? (v / maxMonth) * 100 : 0);
                return (
                  <div key={m.ym} className="flex items-center gap-3">
                    <div className="w-20 shrink-0 text-xs font-medium text-slate-600 capitalize">{monthLabel(m.ym)}</div>
                    <div className="flex-1 h-6 rounded-lg bg-slate-100 overflow-hidden flex">
                      <div className="h-full bg-indigo-500" style={{ width: `${pct(m.athlete_plans)}%` }} title={`Abonamente: ${ron(m.athlete_plans)}`} />
                      <div className="h-full bg-teal-500" style={{ width: `${pct(m.lives)}%` }} title={`Vieți: ${ron(m.lives)}`} />
                      <div className="h-full bg-amber-500" style={{ width: `${pct(m.club_invoices)}%` }} title={`Facturi club: ${ron(m.club_invoices)}`} />
                    </div>
                    <div className="w-28 shrink-0 text-right text-sm font-semibold text-slate-700 tabular-nums">{ron(total)}</div>
                  </div>
                );
              })}
            </div>
          )}
          <p className="mt-4 text-[11px] text-slate-400">
            Notă: veniturile din abonamentele sportivilor reflectă abonamentele activate în perioadă (plata inițială).
            Reînnoirile lunare nu sunt încă stocate individual, deci nu apar ca tranzacții separate.
          </p>
        </div>

        {/* Transactions */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h3 className="text-sm font-bold text-slate-700">Tranzacții</h3>
            <div className="flex flex-wrap gap-1">
              {TYPE_FILTERS.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setTxType(f.key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    txType === f.key ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          {loading ? (
            <p className="text-sm text-slate-500">Se încarcă...</p>
          ) : txs.length === 0 ? (
            <p className="text-sm text-slate-500">Nicio tranzacție în perioada selectată.</p>
          ) : (
            <div className="divide-y divide-slate-50">
              {txs.map((tx) => {
                const meta = STREAMS.find((s) => s.key === tx.type);
                const typeLabel = tx.type === "affiliate" ? "Afiliat (cost)" : meta?.label || tx.type;
                const typeColor = tx.type === "affiliate" ? "#c026d3" : meta?.color || "#64748b";
                return (
                  <div key={`${tx.type}-${tx.id}`} className="py-2.5 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">
                        {tx.payer || tx.label || tx.id}
                      </p>
                      <p className="text-xs text-slate-400 truncate">
                        {tx.date ? new Date(tx.date).toLocaleDateString("ro-RO", { day: "2-digit", month: "short", year: "numeric" }) : ""}
                        {" · "}
                        <span style={{ color: typeColor }}>{typeLabel}</span>
                        {tx.payer && tx.label ? <span className="text-slate-400"> · {tx.label}</span> : null}
                        {tx.billing_reason === "subscription_cycle" ? <span className="text-slate-400"> · reînnoire</span> : null}
                      </p>
                    </div>
                    <span className={`text-sm font-semibold whitespace-nowrap tabular-nums ${tx.is_cost ? "text-rose-600" : "text-slate-700"}`}>
                      {tx.is_cost ? "−" : ""}{ron(tx.amount_ron_cents ?? tx.amount_cents)}
                      {(tx.currency || "").toUpperCase() === "EUR" && (
                        <span className="block text-[10px] font-normal text-slate-400 text-right">
                          {(tx.amount_cents / 100).toLocaleString("ro-RO", { minimumFractionDigits: 2 })} EUR
                        </span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
